import * as React from 'react';

import { NewButton } from '../../../ui/NewButton/NewButton';
import styles from './ImageLightbox.module.css';

interface ImageLightboxProps {
    src: string;
    onClose: () => void;
}

const ZOOM_MIN = 1;
const ZOOM_MAX = 4;
const ZOOM_STEP_BUTTON = 1.25;
const ZOOM_STEP_WHEEL = 1.1;

function openSrcInNewTab(src: string) {
    if (src.startsWith('data:')) {
        try {
            const [header, b64] = src.split(',');
            const mime = (header.match(/:(.*?);/) || [])[1] || 'image/png';
            const bytes = atob(b64);
            const arr = new Uint8Array(bytes.length);
            for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
            const blobUrl = URL.createObjectURL(new Blob([arr], { type: mime }));
            window.open(blobUrl, '_blank');
            setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
        } catch {
            window.open(src, '_blank');
        }
    } else {
        window.open(src, '_blank', 'noreferrer');
    }
}

function touchDistance(touches: TouchList) {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.hypot(dx, dy);
}

/**
 * Полноэкранный просмотр картинки с зумом (колесо мыши, кнопки, pinch) и
 * панорамированием при увеличении (drag мышью или пальцем). Общий компонент
 * для обоих порталов.
 */
export const ImageLightbox: React.FC<ImageLightboxProps> = ({ src, onClose }) => {
    const [zoom, setZoom] = React.useState(1);
    const [pan, setPan] = React.useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = React.useState(false);
    const dragStartRef = React.useRef({ x: 0, y: 0, panX: 0, panY: 0 });
    const imgRef = React.useRef<HTMLImageElement>(null);
    const contentRef = React.useRef<HTMLDivElement>(null);

    // Актуальные zoom/pan для тач-обработчиков — эффект вешается один раз при
    // монтировании (не пересоздаёт listener'ы на каждый setState во время жеста).
    const zoomRef = React.useRef(zoom);
    zoomRef.current = zoom;
    const panRef = React.useRef(pan);
    panRef.current = pan;

    React.useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                onClose();
            }
        };
        // PrimeReact Dialog слушает Escape через свой глобальный приоритетный
        // реестр (useGlobalOnEscapeKey), а не обычный bubbling — у него один
        // document-листенер, который срабатывает независимо от stopPropagation
        // на чужих bubble-листенерах. Capture-фаза гарантированно отрабатывает
        // раньше него независимо от порядка монтирования, поэтому глушим Esc здесь.
        document.addEventListener('keydown', onKey, true);
        return () => {
            document.body.style.overflow = prev;
            document.removeEventListener('keydown', onKey, true);
        };
    }, [onClose]);

    // JSX-овский onWheel вешается пассивно, поэтому preventDefault() в нём не
    // сработает — колесо нужно ловить через addEventListener вручную.
    React.useEffect(() => {
        const el = contentRef.current;
        if (!el) return;
        const onWheel = (e: WheelEvent) => {
            e.preventDefault();
            const factor = e.deltaY < 0 ? ZOOM_STEP_WHEEL : 1 / ZOOM_STEP_WHEEL;
            setZoom((z) => {
                const next = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, +(z * factor).toFixed(3)));
                if (next === ZOOM_MIN) setPan({ x: 0, y: 0 });
                return next;
            });
        };
        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
    }, []);

    const clampPan = React.useCallback((next: { x: number; y: number }, z: number) => {
        const img = imgRef.current;
        const container = contentRef.current;
        if (!img || !container) return next;
        const maxX = Math.max(0, (img.offsetWidth * z - container.clientWidth) / 2);
        const maxY = Math.max(0, (img.offsetHeight * z - container.clientHeight) / 2);
        return {
            x: Math.min(maxX, Math.max(-maxX, next.x)),
            y: Math.min(maxY, Math.max(-maxY, next.y)),
        };
    }, []);

    // Тач: один палец — панорамирование (при zoom > 1), два пальца — pinch-zoom.
    // Как и колесо, вешается вручную с passive: false, иначе preventDefault()
    // не остановит нативный скролл/пинч страницы.
    React.useEffect(() => {
        const el = imgRef.current;
        if (!el) return;

        const gestureRef = { current: null as null | { mode: 'pan'; x: number; y: number; panX: number; panY: number } | { mode: 'pinch'; dist: number; zoom: number } };

        const onTouchStart = (e: TouchEvent) => {
            if (e.touches.length === 2) {
                e.preventDefault();
                gestureRef.current = { mode: 'pinch', dist: touchDistance(e.touches), zoom: zoomRef.current };
                setIsDragging(true);
            } else if (e.touches.length === 1 && zoomRef.current > ZOOM_MIN) {
                e.preventDefault();
                const t = e.touches[0];
                gestureRef.current = { mode: 'pan', x: t.clientX, y: t.clientY, panX: panRef.current.x, panY: panRef.current.y };
                setIsDragging(true);
            }
        };

        const onTouchMove = (e: TouchEvent) => {
            const gesture = gestureRef.current;
            if (!gesture) return;
            if (gesture.mode === 'pinch' && e.touches.length === 2) {
                e.preventDefault();
                const factor = touchDistance(e.touches) / gesture.dist;
                setZoom(() => {
                    const next = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, +(gesture.zoom * factor).toFixed(3)));
                    if (next === ZOOM_MIN) setPan({ x: 0, y: 0 });
                    return next;
                });
            } else if (gesture.mode === 'pan' && e.touches.length === 1) {
                e.preventDefault();
                const t = e.touches[0];
                const dx = t.clientX - gesture.x;
                const dy = t.clientY - gesture.y;
                setPan(clampPan({ x: gesture.panX + dx, y: gesture.panY + dy }, zoomRef.current));
            }
        };

        const onTouchEnd = () => {
            gestureRef.current = null;
            setIsDragging(false);
        };

        el.addEventListener('touchstart', onTouchStart, { passive: false });
        el.addEventListener('touchmove', onTouchMove, { passive: false });
        el.addEventListener('touchend', onTouchEnd);
        el.addEventListener('touchcancel', onTouchEnd);
        return () => {
            el.removeEventListener('touchstart', onTouchStart);
            el.removeEventListener('touchmove', onTouchMove);
            el.removeEventListener('touchend', onTouchEnd);
            el.removeEventListener('touchcancel', onTouchEnd);
        };
    }, [clampPan]);

    React.useEffect(() => {
        if (!isDragging) return;
        const onMouseMove = (e: MouseEvent) => {
            const dx = e.clientX - dragStartRef.current.x;
            const dy = e.clientY - dragStartRef.current.y;
            setPan(clampPan({ x: dragStartRef.current.panX + dx, y: dragStartRef.current.panY + dy }, zoom));
        };
        const onMouseUp = () => setIsDragging(false);
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
        return () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };
    }, [isDragging, zoom, clampPan]);

    const onImgMouseDown = (e: React.MouseEvent) => {
        if (zoom <= ZOOM_MIN) return;
        e.preventDefault();
        dragStartRef.current = { x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y };
        setIsDragging(true);
    };

    const zoomIn = () => setZoom((z) => Math.min(ZOOM_MAX, +(z * ZOOM_STEP_BUTTON).toFixed(3)));
    const zoomOut = () =>
        setZoom((z) => {
            const next = Math.max(ZOOM_MIN, +(z * 0.8).toFixed(3));
            if (next === ZOOM_MIN) setPan({ x: 0, y: 0 });
            return next;
        });
    const zoomReset = () => {
        setZoom(1);
        setPan({ x: 0, y: 0 });
    };

    return (
        <div className={styles.overlay} onClick={onClose}>
            <NewButton
                icon="fa-solid fa-xmark"
                title="Закрыть"
                onClick={onClose}
                style={{
                    position: 'absolute',
                    top: 16,
                    right: 16,
                    width: 40,
                    height: 40,
                    padding: 0,
                    borderRadius: '50%',
                    border: '1.5px solid rgba(255,255,255,0.3)',
                    background: 'rgba(255,255,255,0.12)',
                    color: '#fff',
                    backdropFilter: 'blur(6px)',
                    zIndex: 1
                }}
            />
            <div className={styles.content} ref={contentRef} onClick={(e) => e.stopPropagation()}>
                <img
                    ref={imgRef}
                    className={`${styles.img}${zoom > ZOOM_MIN ? ` ${styles.imgZoomed}` : ``}${isDragging ? ` ${styles.imgDragging}` : ``}`}
                    src={src}
                    alt=""
                    style={{ transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)` }}
                    onMouseDown={onImgMouseDown}
                />
            </div>
            <div className={styles.actions} onClick={(e) => e.stopPropagation()}>
                <NewButton
                    icon="fa-solid fa-magnifying-glass-minus"
                    tooltip="Уменьшить"
                    onClick={zoomOut}
                    disabled={zoom <= ZOOM_MIN}
                />
                <span className={styles.zoomLabel}>{Math.round(zoom * 100)}%</span>
                <NewButton
                    icon="fa-solid fa-magnifying-glass-plus"
                    tooltip="Увеличить"
                    onClick={zoomIn}
                    disabled={zoom >= ZOOM_MAX}
                />
                <span className={styles.actionsDivider} />
                <NewButton
                    icon="fa-solid fa-arrows-rotate"
                    tooltip="Сбросить масштаб"
                    onClick={zoomReset}
                    disabled={zoom === ZOOM_MIN}
                />
                <NewButton
                    variant="secondary"
                    icon="fa-solid fa-arrow-up-right-from-square"
                    onClick={() => openSrcInNewTab(src)}
                >
                    Открыть в новой вкладке
                </NewButton>
            </div>
        </div>
    );
};
